import { prepareNavigationTicket } from "@/lib/api-client/navigation-ticket";
import { get, put } from "@/lib/api-client/request-helpers";
import { t } from "@lingui/core/macro";

export const DEFAULT_NOTIFICATIONS_LIMIT = 20;
const NOTIFICATIONS_STREAM_PATH = "/api/notifications/stream";

export type NotificationType = "alert" | "update";

export type CalendarNotificationAction = {
  kind: "calendar_link";
  /** `YYYY-MM-DD` target for the calendar route. */
  date: string;
  /** Optional `HH:mm` slot to scroll to in calendar day/week view. */
  startTime?: string;
  /** Optional location to select before opening calendar target. */
  locationId?: number;
  /** Optional appointment id to scroll directly to. */
  appointmentId?: number;
  /** Already-localized label shown on the chip. */
  label: string;
};

export type NotificationAction =
  | CalendarNotificationAction
  | { kind: "customer_link"; customerId: number; label: string }
  | { kind: "freeze_requests_link"; label: string };

export type NotificationItem = {
  id: number;
  tenantId: number;
  title: string;
  message: string;
  type: NotificationType;
  isRead: boolean;
  createdAt: string;
  readAt: null | string;
  /** Server-rendered structured actions (e.g. calendar shortcuts). */
  actions: NotificationAction[];
};

type GetNotificationsEndpoint = {
  path: "/api/notifications";
  query: {
    limit?: number;
    cursorCreatedAt?: string;
    cursorId?: number;
  };
  response: {
    notifications: NotificationItem[];
    hasMore: boolean;
    nextCursor: null | NotificationsCursor;
  };
};

type PutNotificationReadEndpoint = {
  path: `/api/notifications/${number}/read`;
  response: {
    success: boolean;
  };
};

type PutNotificationsReadAllEndpoint = {
  path: "/api/notifications/read-all";
  response: {
    success: boolean;
    updatedCount: number;
  };
};

type GetNotificationsUnreadCountEndpoint = {
  path: "/api/notifications/unread-count";
  response: {
    unreadCount: number;
  };
};

export type NotificationsCursor = {
  createdAt: string;
  id: number;
};

export type NotificationsPage = {
  notifications: NotificationItem[];
  hasMore: boolean;
  nextCursor: null | NotificationsCursor;
};

export async function fetchNotificationsPage({
  limit = DEFAULT_NOTIFICATIONS_LIMIT,
  cursor,
}: {
  limit?: number;
  cursor: NotificationsCursor | null;
}) {
  const response = await get<GetNotificationsEndpoint>("/api/notifications", {
    limit,
    cursorCreatedAt: cursor?.createdAt,
    cursorId: cursor?.id,
  });

  return {
    ...response,
    notifications: sortNotificationsByCreatedAt(response.notifications),
  } satisfies NotificationsPage;
}

export async function markNotificationAsRead(notificationId: number) {
  await put<PutNotificationReadEndpoint>(
    `/api/notifications/${notificationId}/read`,
  );
}

export async function markAllNotificationsAsRead() {
  await put<PutNotificationsReadAllEndpoint>("/api/notifications/read-all");
}

export async function fetchUnreadNotificationsCount() {
  const response = await get<GetNotificationsUnreadCountEndpoint>(
    "/api/notifications/unread-count",
  );

  return response.unreadCount;
}

/**
 * EventSource cannot set `x-access-token`, so the stream authenticates with the
 * navigation-ticket cookie dropped here rather than the session JWT (DEV-9694).
 * The EventSource must be opened with `withCredentials` for the browser to send
 * it cross-origin.
 */
export async function buildNotificationsStreamUrl() {
  await prepareNavigationTicket("notifications-stream");

  const streamPath = NOTIFICATIONS_STREAM_PATH;
  const backendHost = import.meta.env.VITE_BE_HOST;

  if (!backendHost) {
    return streamPath;
  }

  try {
    return new URL(streamPath, backendHost).toString();
  } catch (error) {
    return `${backendHost.replace(/\/$/, "")}${streamPath}`;
  }
}

export function sortNotificationsByCreatedAt(
  notifications: NotificationItem[],
) {
  return [...notifications].sort((left, right) => {
    return (
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  });
}

export function formatNotificationDate(createdAt: string) {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return createdAt;
  }

  return date.toLocaleString();
}

/**
 * Keeps only actions this client knows how to follow. A kind added by a newer
 * server is dropped, so the notification still renders without that chip.
 */
export function isNotificationAction(
  value: unknown,
): value is NotificationAction {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const action = value as Record<string, unknown>;
  if (typeof action.label !== "string") {
    return false;
  }
  switch (action.kind) {
    case "calendar_link":
      return hasCalendarActionFields(action);
    case "customer_link":
      return Number.isInteger(action.customerId);
    case "freeze_requests_link":
      return true;
    default:
      return false;
  }
}

function hasCalendarActionFields(action: Record<string, unknown>): boolean {
  return (
    typeof action.date === "string" &&
    isOptional(action.startTime, "string") &&
    isOptional(action.appointmentId, "number") &&
    isOptional(action.locationId, "number")
  );
}

function isOptional(value: unknown, type: "string" | "number"): boolean {
  return value == null || typeof value === type;
}

export function buildNotificationActionLink(
  action: NotificationAction,
): string {
  switch (action.kind) {
    case "calendar_link":
      return buildCalendarLinkForAction(action);
    case "customer_link":
      return `/dashboard/customers/${action.customerId}`;
    case "freeze_requests_link":
      return "/dashboard/customer-subscriptions-overview/freeze-requests";
  }
}

/**
 * Builds the calendar route for a `YYYY-MM-DD` date. Used by the
 * notification renderer to make double-booking slot dates clickable.
 * encodeURIComponent is defence-in-depth — the date is structured.
 */
function buildCalendarLinkForAction(
  action: CalendarNotificationAction,
): string {
  const query = new URLSearchParams({
    date: action.date,
  });

  if (action.startTime) {
    query.set("scrollTime", action.startTime);
  }

  if (action.locationId != null) {
    query.set("locationId", action.locationId.toString());
  }

  if (action.appointmentId != null) {
    query.set("scrollApptId", action.appointmentId.toString());
  }

  return `/dashboard/calendar?${query.toString()}`;
}

export function formatRelativeNotificationDate(createdAt: string) {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return createdAt;
  }

  const diffSeconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (diffSeconds < 5) {
    return t`just now`;
  }
  if (diffSeconds < 60) {
    return t`${diffSeconds}s ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return t`${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return t`${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return t`${diffDays}d ago`;
  }

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year:
      date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}
